import { type ReactNode } from 'react';

import { Box } from '@mui/material';

import { type HandoverSpec } from '@/designs-dev/handover/types';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import {
  RECIPIENT,
  type ChatQuestion,
} from '@/karehero-app/tell-us-once/intake/content';
import { INTAKE_CHAT_V2_0_DEV } from '@/karehero-app/tell-us-once/intake/content.v2-0-dev';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import AppTab from '@/karehero-app/shell/AppTab';
import AccountBar from '@/karehero-app/access/AccountBar';
import StatusBar from '@/designs-dev/device/StatusBar';
import HomeConcept2 from '@/karehero-app/tell-us-once/intake/HomeConcept2';

const noop = () => {};

// Onboarding 2.0's own person, so the slices that carry on from its chat
// read as the same conversation.
export const NAME = RECIPIENT;

// What we already hold, said back as the options' own labels.
export const HELD = [
  'Lives in England',
  '66 or over',
  'Needs help day and night',
];

const HEARD_CARD = {
  label: 'Does this look right?',
  items: HELD,
  sentence: true,
  editable: true,
};

// The brief playing out: what we hold, checked, then only what is left.
export const WITH_HELD: ChatQuestion[] = [
  {
    id: 'heard',
    says: [`Before we start, here's what we already know about ${NAME}.`],
    card: HEARD_CARD,
    ask: 'Does this look right?',
    mode: 'say',
    quickReplies: [{ label: "Yes, that's right" }, { label: 'Not quite' }],
    answer: "Yes, that's right",
  },
  {
    id: 'nursing-home',
    ask: `Thanks. Is ${NAME} living in a care or nursing home?`,
    mode: 'say',
    quickReplies: [{ label: 'Yes' }, { label: 'No' }],
  },
];

// Holding nothing: no card, straight to the first question in the brief.
const NOTHING_HELD: ChatQuestion[] = [
  {
    id: 'location',
    // The opening line is main's own, from onboarding V4's copy.
    says: [
      `Just a few quick questions, so we can check what ${NAME} could be entitled to.`,
    ],
    ask: `Some benefits work differently in each part of the UK, so we need to know where ${NAME} lives.`,
    mode: 'say',
    attachRows: {},
    asides: [
      { label: 'England' },
      { label: 'Scotland' },
      { label: 'Wales' },
      { label: 'Northern Ireland' },
      { label: 'Outside the UK' },
    ],
  },
];

/**
 * The end of Onboarding 2.0's chat, as it is on dev, with its last turn (where
 * to start) swapped for another. Everything before it is 2.0's own transcript,
 * so the slice reads as onboarding carrying on, not a new start.
 */
const endingWith = (last: ChatQuestion): ChatQuestion[] =>
  INTAKE_CHAT_V2_0_DEV.map((q) => (q.id === 'start' ? last : q));

const lastTurn = (questions: ChatQuestion[]) =>
  questions.findIndex((q) => q.id === 'start');

// Slice 2: what they told us points at money, so the calculator is offered
// where onboarding would ask where to start.
const OFFER_CALCULATOR = endingWith({
  id: 'start',
  ask: `Great, thank you. From what you've told us, help with money could make a real difference. Shall we work out what ${NAME} could be entitled to? It's a few quick questions.`,
  mode: 'say',
  attachRows: { more: ['Do this later'] },
  asides: [{ label: `Work out what ${NAME} could claim` }],
});

// Slice 5: the same last turn, another route. Care at home rather than money.
const OFFER_CARE = endingWith({
  id: 'start',
  ask: `Great, thank you. From what you've told us, finding care at home sounds like the priority. Shall we look at carers near ${NAME}?`,
  mode: 'say',
  attachRows: { more: ['Do this later'] },
  asides: [{ label: 'Find carers near them' }],
});

// Drawn as Onboarding 2.0 draws its chat: the logo bar, the mic beside
// the field, and its reply style: dark gardenia bubbles with charcoal text.
// Home is a tab, so the Home scenes keep the tab bar under them.
// The page alone, under 2.0's bar, with no composer: Home here is somewhere to
// go from, not a conversation.
export const homeTab = (page: ReactNode) => (
  <AppTab active='home'>
    <StatusBar />
    <AccountBar onSignOut={noop} />
    <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 2 }}>{page}</Box>
  </AppTab>
);

export const chat = (
  questions: ChatQuestion[],
  startAt = 0,
  emptyState?: ReactNode,
) => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <ChatV2
      questions={questions}
      startAt={startAt}
      onExit={noop}
      account={{ onSignOut: noop }}
      inlineMic
      emptyState={emptyState}
    />
  </IntakeStyleContext.Provider>
);

/**
 * The slices in the Notion doc, drawn with Onboarding 2.0's chat. Visuals only:
 * the doc says what each slice is. Slice 1 (ship V4) and slice 6 (onboarding
 * moves in) look the same as V4 to the person using it, so neither has a row.
 */
export const dynamicFlowSlicesSpec: HandoverSpec = {
  id: 'dynamic-flow-slices',
  title: 'Dynamic flows, in slices',
  description: '',
  canvas: true,
  links: [
    {
      label: 'Dynamic flows → Benefits calc (Notion)',
      url: 'https://app.notion.com/p/karehero/Dynamic-flows-Benefits-calc-3fb63e8971dd8077b6b7e39eb33b02f7',
    },
    {
      label: 'Dynamic flow concepting',
      url: '/tell-us-once/flows/dynamic-flow',
    },
    {
      label: 'Benefits calculator',
      url: '/tell-us-once/flows/benefits-calculator',
    },
  ],
  flows: [
    {
      // Left to right, one column per slice; the component sits under the
      // calculator it belongs to.
      title: 'Slice 2: one goal',
      arrows: false,
      steps: [
        {
          id: 'goal-today',
          title: 'Where onboarding 4 ends today',
          render: () =>
            chat(INTAKE_CHAT_V2_0_DEV, lastTurn(INTAKE_CHAT_V2_0_DEV)),
        },
        {
          id: 'goal-offer',
          title: 'From what they told us, the calculator',
          render: () => chat(OFFER_CALCULATOR, lastTurn(OFFER_CALCULATOR)),
          // The other way in, unchanged: the calculator from Home, as it is.
          alt: {
            title: 'Or from Home, as normal',
            render: () => (
              <AppTab active='home'>
                <HomeScreen />
              </AppTab>
            ),
          },
        },
      ],
    },
    {
      title: 'Slice 3: the calculator, as a brief',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'brief-heard',
          title: 'What we already hold, to check',
          render: () => chat(WITH_HELD),
          alt: {
            title: 'If we hold nothing, no card',
            render: () => chat(NOTHING_HELD),
          },
        },
        {
          id: 'brief-next',
          title: 'Only asked because they live in England',
          render: () => chat(WITH_HELD, 1),
        },
      ],
    },
    {
      title: 'Slice 3: the onboarding component it reuses',
      under: true,
      arrows: false,
      // In the readme's place rather than as a step, so it is drawn bare: in a
      // phone frame it read as a screen of its own.
      note: (
        <FromUsCard
          label={HEARD_CARD.label}
          items={HELD}
          sentence
          onEdit={noop}
        />
      ),
      steps: [],
    },
    {
      title: 'Slice 4: the app in between',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'between-home',
          title: 'Home, as it is today',
          render: () => (
            <AppTab active='home'>
              <HomeScreen />
            </AppTab>
          ),
        },
        {
          // An idea, from Concept 3 in Dynamic flow concepting: the calculator
          // as a checklist item on Home that starts its own conversation.
          id: 'between-checklist',
          title: 'An idea: the calculator, on a card on Home',
          render: () =>
            homeTab(
              <HomeConcept2
                todo
                conversations={false}
                todoList={{
                  label: 'Things we can help with',
                  body: `From what you've told us, we could help you work out what you and ${NAME} are entitled to.`,
                  items: [],
                  action: 'Check eligibility',
                }}
              />,
            ),
          // More than one, picked and ordered by what they told us.
          alt: [
            {
              title: "Or more, depending on what they've told us",
              render: () =>
                homeTab(
                  <HomeConcept2
                    todo
                    conversations={false}
                    todoList={{
                      label: 'Things we can help with',
                      items: [
                        `Check what you and ${NAME} could be entitled to`,
                        `Find carers near ${NAME}`,
                        'Book a call with a care expert',
                      ],
                      onItem: noop,
                      more: 'See everything we can help with',
                    }}
                  />,
                ),
            },
            {
              // The same card as scene 2, with the chat's answer rows: choices,
              // not a checklist that reads as progress.
              title: "Or more, as the chat's answer rows",
              render: () =>
                homeTab(
                  <HomeConcept2
                    todo
                    conversations={false}
                    todoList={{
                      label: 'Things we can help with',
                      body: "From what you've told us, we could help with these.",
                      items: [],
                      options: [
                        `Check what you and ${NAME} could be entitled to`,
                        `Find carers near ${NAME}`,
                        'Book a call with a care expert',
                      ],
                    }}
                  />,
                ),
            },
          ],
        },
        {
          id: 'between-tapped',
          title: 'Tapped, it starts the calculator',
          render: () => chat(WITH_HELD),
        },
      ],
    },
    {
      title: 'Slice 5: more routes, one at a time',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'route-care',
          title: 'Another route: care at home',
          render: () => chat(OFFER_CARE, lastTurn(OFFER_CARE)),
        },
      ],
    },
  ],
  states: [],
  components: {},
};
